import { WidgetId } from 'types/idTypes';
import { WidgetsMap } from 'types/infographTypes';
import { GroupWidgetData } from 'widgets/GroupWidget/GroupWidget.types';
import { getWidgetRect } from 'widgets/WidgetLayout.helpers';
import { WidgetRect } from 'widgets/WidgetLayout.types';

import { Rect } from './regionGrowing.types';

/**
 * Helper to convert a `WidgetRect` object to a `Rect` object.
 *
 * @param widgetRect - The `WidgetRect` object to convert.
 * @param id - The ID of the `Rect` object.
 * @returns The converted `Rect` object.
 */
const widgetRectToRect = (widgetRect: WidgetRect, id: WidgetId): Rect => {
  return {
    id,
    // Round to down to whole numbers to make the calculations a bit easier
    x: Math.floor(widgetRect.leftPx),
    y: Math.floor(widgetRect.topPx),
    width: Math.floor(widgetRect.widthPx),
    height: Math.floor(widgetRect.heightPx),
  };
};

/**
 * RegionGrowing algorithm:
 * Groups rectangles that are adjacent to each other within the given proximity threshold.
 *
 * @param rectArr - An array of rects.
 * @param proximityThreshold - The proximity threshold used to determine if two rectangles are adjacent.
 * @returns An array of groups, where each group contains a set of adjacent rectangles.
 */
export const regionGrowing = (
  widgetIdArr: WidgetId[],
  proximityThreshold: number,
  groupParentMap: { [WidgetId: WidgetId]: string },
  widgets: WidgetsMap,
) => {
  // turn the widgetRect arr into a rectArr to make it a bit easier for the calculations (x,y instead of leftPx, topPx etc)
  const rectArr = widgetIdArr.map((widgetId) => widgetRectToRect(getWidgetRect(widgets[widgetId]), widgetId));
  const groups: any[][] = [];
  // a rect can only belong to one group
  const processed = new Set<Rect>();

  for (const rect of rectArr) {
    if (processed.has(rect)) continue;

    const group = [rect];
    processed.add(rect);
    const stack = [rect];

    // check all neighbors of the current rect
    while (stack.length > 0) {
      // pop the last rect from the stack
      const current = stack.pop()!;

      // Find the adjacents rect within the proximityTreshhold to the current rect
      const neighbors = rectArr.filter(
        (otherRect) => otherRect !== current && isAdjacent(current, otherRect, proximityThreshold),
      );

      for (const neighbor of neighbors) {
        if (!processed.has(neighbor)) {
          // add the neighbor to the group
          group.push(neighbor);
          processed.add(neighbor);

          // if it is a group member - add the rest of the group as well
          const groupId = groupParentMap[neighbor.id];
          if (groupId) {
            const memberWidgetIds = (widgets[groupId] as GroupWidgetData).memberWidgetIds;
            for (const memberId of memberWidgetIds) {
              const memberRect = widgetRectToRect(getWidgetRect(widgets[memberId]), memberId);
              if (!processed.has(memberRect)) {
                group.push(memberRect);
                processed.add(memberRect);
              }
            }
          }

          // add the neighbor to the stack so we can check if the neighbor has neighbors adjacent to it
          stack.push(neighbor);
        }
      }
    }

    groups.push(group);
  }

  return groups;
};

/**
 * Checks if two rectangles are adjacent based on a given threshold
 *
 * @param a - The first rectangle.
 * @param b - The second rectangle.
 * @param threshold - The threshold value for adjacency.
 * @returns True if the rectangles are adjacent, false otherwise.
 */
export const isAdjacent = (a: Rect, b: Rect, threshold: number) => {
  // Check for overlap or containment - if so A and B are adjacent
  const isOverlapping = a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
  if (isOverlapping) return true;

  // Calculate the coordinates of the corners
  const aLeft = a.x;
  const aRight = a.x + a.width;
  const aTop = a.y;
  const aBottom = a.y + a.height;
  const bLeft = b.x;
  const bRight = b.x + b.width;
  const bTop = b.y;
  const bBottom = b.y + b.height;

  // Find the closest points between the two rectangles
  const closestX = Math.max(aLeft, Math.min(bLeft, aRight)) - Math.max(bLeft, Math.min(aLeft, bRight));
  const closestY = Math.max(aTop, Math.min(bTop, aBottom)) - Math.max(bTop, Math.min(aTop, bBottom));

  // Calculate the Euclidean distance
  const distance = Math.sqrt(closestX * closestX + closestY * closestY);

  return distance <= threshold;
};
